import React from "react";

import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Check,
  CircleCheck,
  CircleHelp,
  Euro,
  ExternalLink,
  FileText,
  Gauge,
  Info,
  Link2,
  Loader2,
  Mail,
  MapPin,
  Percent,
  Phone,
  Save,
  ShieldCheck,
  UploadCloud,
  UserRound,
  X,
} from "lucide-react";

import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { useToast } from "../../../components/feedback/ToastProvider";

import "./ExternalRenovationPage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   EMPTY FORM
   ========================================================= */

const EMPTY_FORM = {
  external_oss_name: "",
  external_oss_country_code: "",
  external_oss_contact_name: "",
  external_oss_email: "",
  external_oss_phone: "",
  external_oss_website: "",

  renovation_summary: "",
  planned_renovation_cost: "",

  expected_start_date: "",
  expected_completion_date: "",

  notes: "",
};

const EMPTY_OUTCOME = {
  actual_start_date: "",
  actual_completion_date: "",
  actual_renovation_cost: "",

  completed_works_summary: "",
  completion_notes: "",

  final_epc_label: "",
  final_epc_energy_score: "",
  final_epc_certificate_number: "",
  final_epc_issue_date: "",

  estimated_energy_savings_percent: "",
  estimated_co2_reduction_percent: "",
};

/* =========================================================
   ANIMATIONS
   ========================================================= */

const pageVariants = {
  hidden: {
    opacity: 0,
  },

  visible: {
    opacity: 1,

    transition: {
      duration: 0.32,
      when: "beforeChildren",
      staggerChildren: 0.07,
    },
  },
};

const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 18,
  },

  visible: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 0.46,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* =========================================================
   HELPERS
   ========================================================= */

function normalizeRenovationToForm(renovation) {
  if (!renovation) {
    return {
      ...EMPTY_FORM,
    };
  }

  return {
    external_oss_name: renovation.external_oss_name || "",

    external_oss_country_code: renovation.external_oss_country_code || "",

    external_oss_contact_name: renovation.external_oss_contact_name || "",

    external_oss_email: renovation.external_oss_email || "",

    external_oss_phone: renovation.external_oss_phone || "",

    external_oss_website: renovation.external_oss_website || "",

    renovation_summary: renovation.renovation_summary || "",

    planned_renovation_cost: renovation.planned_renovation_cost || "",

    expected_start_date: renovation.expected_start_date || "",

    expected_completion_date: renovation.expected_completion_date || "",

    notes: renovation.notes || "",
  };
}

function normalizeOutcomeToForm(renovation) {
  const outcome = renovation?.outcome;

  if (!outcome) {
    return {
      ...EMPTY_OUTCOME,
    };
  }

  return {
    actual_start_date: outcome.actual_start_date || "",
    actual_completion_date: outcome.actual_completion_date || "",
    actual_renovation_cost: outcome.actual_renovation_cost || "",

    completed_works_summary: outcome.completed_works_summary || "",
    completion_notes: outcome.completion_notes || "",

    final_epc_label: outcome.final_epc_label || "",
    final_epc_energy_score: outcome.final_epc_energy_score || "",
    final_epc_certificate_number: outcome.final_epc_certificate_number || "",
    final_epc_issue_date: outcome.final_epc_issue_date || "",

    estimated_energy_savings_percent:
      outcome.estimated_energy_savings_percent || "",

    estimated_co2_reduction_percent:
      outcome.estimated_co2_reduction_percent || "",
  };
}

function hasOutcomeValues(outcome) {
  return Object.values(outcome || {}).some(
    (value) =>
      value !== null && value !== undefined && String(value).trim() !== "",
  );
}

function getFirstApiError(payload) {
  if (!payload) {
    return "";
  }

  if (typeof payload.detail === "string") {
    return payload.detail;
  }

  for (const value of Object.values(payload)) {
    if (Array.isArray(value) && value.length) {
      return String(value[0]);
    }

    if (typeof value === "string") {
      return value;
    }

    if (value && typeof value === "object") {
      const nested = getFirstApiError(value);

      if (nested) {
        return nested;
      }
    }
  }

  return "";
}

async function parseApiResponse(response) {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(getFirstApiError(data) || "Something went wrong.");
  }

  return data;
}

function formatFileSize(size) {
  if (!size) {
    return "";
  }

  const megabytes = Number(size) / 1024 / 1024;

  if (megabytes >= 1) {
    return `${megabytes.toFixed(2)} MB`;
  }

  return `${(Number(size) / 1024).toFixed(0)} KB`;
}

const FORMATTED_OUTCOME_FIELDS = new Set([
  "actual_renovation_cost",
  "final_epc_energy_score",
  "estimated_energy_savings_percent",
  "estimated_co2_reduction_percent",
]);

function sanitizeDecimalInput(value, maxDecimals = 2) {
  let cleaned = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^\d.]/g, "");

  if (!cleaned) {
    return "";
  }

  const dotIndex = cleaned.indexOf(".");

  let integerPart = dotIndex === -1 ? cleaned : cleaned.slice(0, dotIndex);

  let decimalPart =
    dotIndex === -1
      ? ""
      : cleaned
          .slice(dotIndex + 1)
          .replace(/\./g, "")
          .slice(0, maxDecimals);

  integerPart = integerPart.replace(/^0+(?=\d)/, "");

  if (!integerPart && dotIndex !== -1) {
    integerPart = "0";
  }

  return dotIndex === -1 ? integerPart : `${integerPart}.${decimalPart}`;
}

function formatDecimalInput(value) {
  const raw = String(value ?? "").replace(/,/g, "");

  if (!raw) {
    return "";
  }

  const hasDecimalPoint = raw.includes(".");

  const [integerPart, decimalPart = ""] = raw.split(".");

  const formattedInteger = (integerPart || "0").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ",",
  );

  return hasDecimalPoint
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
}

function getEpcLabelOptions(countryCode) {
  const code = String(countryCode || "")
    .trim()
    .toUpperCase();

  // Flemish / Belgian EPC scale
  if (code === "BE") {
    return ["A+", "A", "B", "C", "D", "E", "F"];
  }

  // Spanish energy certificate scale
  if (code === "ES") {
    return ["A", "B", "C", "D", "E", "F", "G"];
  }

  // Generic fallback
  return ["A+", "A", "B", "C", "D", "E", "F", "G"];
}

/* =========================================================
   TOOLTIP
   ========================================================= */

function FieldTooltip({ text, label }) {
  if (!text) {
    return null;
  }

  return (
    <span className="hb-ext-reno__tooltip">
      <button
        type="button"
        className="hb-ext-reno__tooltip-trigger"
        aria-label={label || "Field information"}
      >
        <CircleHelp size={15} strokeWidth={2} />
      </button>

      <span className="hb-ext-reno__tooltip-content" role="tooltip">
        {text}
      </span>
    </span>
  );
}

/* =========================================================
   FIELD
   ========================================================= */

function Field({
  label,
  required = false,
  hint,
  tooltip,
  children,
  wide = false,
}) {
  return (
    <label
      className={["hb-ext-reno__field", wide ? "hb-ext-reno__field--wide" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="hb-ext-reno__field-label">
        <div className="hb-ext-reno__field-label-main">
          <span>
            {label}

            {required && <em>*</em>}
          </span>

          <FieldTooltip text={tooltip} label={label} />
        </div>

        {hint && <small>{hint}</small>}
      </div>

      {children}
    </label>
  );
}

/* =========================================================
   SECTION HEADER
   ========================================================= */

function SectionHeader({ number, icon: Icon, title, description }) {
  return (
    <div className="hb-ext-reno__section-header">
      <div className="hb-ext-reno__section-title-row">
        <div className="hb-ext-reno__section-number">{number}</div>

        <div className="hb-ext-reno__section-icon">
          <Icon size={19} />
        </div>

        <h2>{title}</h2>
      </div>

      <p>{description}</p>
    </div>
  );
}

/* =========================================================
   PAGE
   ========================================================= */

export default function ExternalRenovationPage() {
  const { t } = useTranslation("external-renovation");

  const navigate = useNavigate();

  const location = useLocation();

  const {
    isAuthenticated,

    isLoading: isAuthLoading,

    getAccessTokenSilently,
  } = useAuth0();

  const { showToast } = useToast();

  /* =======================================================
     STATE
     ======================================================= */

  const [formData, setFormData] = React.useState({
    ...EMPTY_FORM,
  });

  const [renovation, setRenovation] = React.useState(null);

  const [dashboardSummary, setDashboardSummary] = React.useState(null);

  const [selectedPlan, setSelectedPlan] = React.useState(null);

  const [outcomeData, setOutcomeData] = React.useState({
    ...EMPTY_OUTCOME,
  });

  const [selectedFinalEpc, setSelectedFinalEpc] = React.useState(null);

  const [isLoading, setIsLoading] = React.useState(true);

  const [isSaving, setIsSaving] = React.useState(false);

  const [isLinking, setIsLinking] = React.useState(false);

  const [isOpeningPlan, setIsOpeningPlan] = React.useState(false);

  const [isOpeningFinalEpc, setIsOpeningFinalEpc] = React.useState(false);

  const fileInputRef = React.useRef(null);

  const finalEpcInputRef = React.useRef(null);

  /* =======================================================
     FINANCING CONTEXT
     ======================================================= */

  const dossierId =
    location.state?.dossierId || dashboardSummary?.dossier_id || null;

  const loanCaseId =
    location.state?.loanCaseId || dashboardSummary?.loan_case_id || null;

  const dossierCode =
    dashboardSummary?.dossier_code || renovation?.dossier_code || "";

  const loanCaseReference = dashboardSummary?.loan_case_reference || "";

  const hasAvailableContext = Boolean(dossierId || loanCaseId);

  const isLinked = Boolean(
    renovation?.is_linked_to_dossier || renovation?.dossier_id,
  );

  /* =======================================================
     LOAD PAGE
     ======================================================= */

  const loadPageData = React.useCallback(async () => {
    if (isAuthLoading || !isAuthenticated) {
      return;
    }

    try {
      setIsLoading(true);

      const accessToken = await getAccessTokenSilently();

      const [meResponse, renovationsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/homeowner/me/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,

            Accept: "application/json",
          },
        }),

        fetch(`${API_BASE_URL}/api/homeowner/external-renovations/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,

            Accept: "application/json",
          },
        }),
      ]);

      const meData = await parseApiResponse(meResponse);

      const renovationsData = await parseApiResponse(renovationsResponse);

      const summary = meData?.dashboard_summary || {};

      setDashboardSummary(summary);

      const renovations = renovationsData?.external_renovations || [];

      const currentDossierId =
        location.state?.dossierId || summary?.dossier_id || null;

      let currentRenovation = null;

      /* ---------------------------------------------
             Prefer renovation linked to current dossier
             --------------------------------------------- */

      if (currentDossierId) {
        currentRenovation =
          renovations.find(
            (item) =>
              item.dossier_id &&
              Number(item.dossier_id) === Number(currentDossierId),
          ) || null;
      }

      /* ---------------------------------------------
             Otherwise prefer unlinked renovation
             --------------------------------------------- */

      if (!currentRenovation) {
        currentRenovation =
          renovations.find((item) => !item.dossier_id) || null;
      }

      /* ---------------------------------------------
             Single-record fallback
             --------------------------------------------- */

      if (!currentRenovation && renovations.length === 1) {
        currentRenovation = renovations[0];
      }

      if (currentRenovation) {
        setRenovation(currentRenovation);

        setFormData(normalizeRenovationToForm(currentRenovation));

        setOutcomeData(normalizeOutcomeToForm(currentRenovation));
      } else {
        setRenovation(null);

        setOutcomeData({
          ...EMPTY_OUTCOME,
        });

        setFormData({
          ...EMPTY_FORM,

          external_oss_country_code: (
            summary?.market_region?.country_code ||
            meData?.profile?.country_of_property_purchase ||
            ""
          )
            .toUpperCase()
            .slice(0, 2),
        });
      }
    } catch (error) {
      console.error("Could not load external renovation:", error);

      showToast({
        type: "error",

        title: t(
          "externalRenovation.toast.loadErrorTitle",
          "Could not load renovation",
        ),

        message:
          error?.message ||
          t(
            "externalRenovation.messages.loadError",
            "Could not load the renovation information.",
          ),
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    isAuthLoading,
    location.state?.dossierId,
    showToast,
    t,
  ]);

  React.useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  /* =======================================================
     FORM CHANGE
     ======================================================= */

  const handleChange = React.useCallback((event) => {
    const { name, value } = event.target;

    let nextValue = value;

    if (name === "external_oss_country_code") {
      nextValue = String(value || "")
        .toUpperCase()
        .slice(0, 2);
    }

    if (name === "planned_renovation_cost") {
      nextValue = sanitizeDecimalInput(value, 2);
    }

    setFormData((previous) => ({
      ...previous,
      [name]: nextValue,
    }));
  }, []);

  /* =======================================================
     OUTCOME CHANGE
     ======================================================= */

  const handleOutcomeChange = React.useCallback((event) => {
    const { name, value } = event.target;

    let nextValue = value;

    if (FORMATTED_OUTCOME_FIELDS.has(name)) {
      nextValue = sanitizeDecimalInput(value, 2);
    }

    setOutcomeData((previous) => ({
      ...previous,
      [name]: nextValue,
    }));
  }, []);

  /* =======================================================
     FILE SELECTION
     ======================================================= */

  const handlePlanSelected = React.useCallback(
    (event) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      const allowedTypes = ["application/pdf", "image/png", "image/jpeg"];

      if (!allowedTypes.includes(file.type)) {
        showToast({
          type: "error",

          title: t("externalRenovation.toast.invalidFileTitle", "Invalid file"),

          message: t(
            "externalRenovation.messages.invalidFileType",
            "Please select a PDF, PNG or JPG file.",
          ),
        });

        event.target.value = "";

        return;
      }

      if (file.size > 20 * 1024 * 1024) {
        showToast({
          type: "error",

          title: t(
            "externalRenovation.toast.fileTooLargeTitle",
            "File too large",
          ),

          message: t(
            "externalRenovation.messages.fileTooLarge",
            "The renovation plan must be smaller than 20 MB.",
          ),
        });

        event.target.value = "";

        return;
      }

      setSelectedPlan(file);
    },
    [showToast, t],
  );

  const clearSelectedPlan = React.useCallback(() => {
    setSelectedPlan(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleFinalEpcSelected = React.useCallback(
    (event) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      const allowedTypes = ["application/pdf", "image/png", "image/jpeg"];

      if (!allowedTypes.includes(file.type)) {
        showToast({
          type: "error",

          title: t(
            "externalRenovation.toast.invalidEpcFileTitle",
            "Invalid file",
          ),

          message: t(
            "externalRenovation.messages.invalidEpcFileType",
            "Please select a PDF, PNG or JPG file for the final EPC.",
          ),
        });

        event.target.value = "";

        return;
      }

      if (file.size > 20 * 1024 * 1024) {
        showToast({
          type: "error",

          title: t(
            "externalRenovation.toast.epcFileTooLargeTitle",
            "File too large",
          ),

          message: t(
            "externalRenovation.messages.epcFileTooLarge",
            "The final EPC document must be smaller than 20 MB.",
          ),
        });

        event.target.value = "";

        return;
      }

      setSelectedFinalEpc(file);
    },
    [showToast, t],
  );

  const clearSelectedFinalEpc = React.useCallback(() => {
    setSelectedFinalEpc(null);

    if (finalEpcInputRef.current) {
      finalEpcInputRef.current.value = "";
    }
  }, []);

  /* =======================================================
     VALIDATION
     ======================================================= */

  const validateForm = React.useCallback(() => {
    if (!formData.external_oss_name.trim()) {
      return t(
        "externalRenovation.validation.ossName",
        "External One-Stop Shop name is required.",
      );
    }

    if (formData.external_oss_country_code.trim().length !== 2) {
      return t(
        "externalRenovation.validation.country",
        "Please provide a valid two-letter country code.",
      );
    }

    if (!formData.renovation_summary.trim()) {
      return t(
        "externalRenovation.validation.summary",
        "Please provide a short description of the renovation.",
      );
    }

    const cost = Number(formData.planned_renovation_cost);

    if (!Number.isFinite(cost) || cost <= 0) {
      return t(
        "externalRenovation.validation.cost",
        "Please provide a valid planned renovation cost.",
      );
    }

    if (
      formData.expected_start_date &&
      formData.expected_completion_date &&
      formData.expected_completion_date < formData.expected_start_date
    ) {
      return t(
        "externalRenovation.validation.dates",
        "The expected completion date cannot be before the expected start date.",
      );
    }

    if (!renovation?.has_plan && !selectedPlan) {
      return t(
        "externalRenovation.validation.plan",
        "Please upload the renovation plan.",
      );
    }

    if (
      outcomeData.actual_start_date &&
      outcomeData.actual_completion_date &&
      outcomeData.actual_completion_date < outcomeData.actual_start_date
    ) {
      return t(
        "externalRenovation.validation.actualDates",
        "The actual completion date cannot be before the actual start date.",
      );
    }

    if (
      outcomeData.final_epc_issue_date &&
      outcomeData.actual_completion_date &&
      outcomeData.final_epc_issue_date < outcomeData.actual_completion_date
    ) {
      return t(
        "externalRenovation.validation.epcIssueDate",
        "The final EPC issue date cannot be before the renovation completion date.",
      );
    }

    if (
      outcomeData.actual_renovation_cost !== "" &&
      (!Number.isFinite(Number(outcomeData.actual_renovation_cost)) ||
        Number(outcomeData.actual_renovation_cost) < 0)
    ) {
      return t(
        "externalRenovation.validation.actualCostNonNegative",
        "Actual renovation cost must be zero or greater.",
      );
    }

    if (
      outcomeData.final_epc_energy_score !== "" &&
      (!Number.isFinite(Number(outcomeData.final_epc_energy_score)) ||
        Number(outcomeData.final_epc_energy_score) < 0)
    ) {
      return t(
        "externalRenovation.validation.finalEpcScoreNonNegative",
        "Final EPC energy score must be zero or greater.",
      );
    }

    if (
      outcomeData.estimated_energy_savings_percent !== "" &&
      (!Number.isFinite(Number(outcomeData.estimated_energy_savings_percent)) ||
        Number(outcomeData.estimated_energy_savings_percent) < 0 ||
        Number(outcomeData.estimated_energy_savings_percent) > 100)
    ) {
      return t(
        "externalRenovation.validation.energySavingsPercent",
        "Estimated energy savings must be between 0 and 100.",
      );
    }

    if (
      outcomeData.estimated_co2_reduction_percent !== "" &&
      (!Number.isFinite(Number(outcomeData.estimated_co2_reduction_percent)) ||
        Number(outcomeData.estimated_co2_reduction_percent) < 0 ||
        Number(outcomeData.estimated_co2_reduction_percent) > 100)
    ) {
      return t(
        "externalRenovation.validation.co2ReductionPercent",
        "Estimated CO₂ reduction must be between 0 and 100.",
      );
    }

    const percentageFields = [
      ["estimated_energy_savings_percent", "Estimated energy savings"],
      ["estimated_co2_reduction_percent", "Estimated CO₂ reduction"],
    ];

    for (const [fieldName, label] of percentageFields) {
      const value = outcomeData[fieldName];

      if (
        value !== "" &&
        (!Number.isFinite(Number(value)) ||
          Number(value) < 0 ||
          Number(value) > 100)
      ) {
        return `${label} must be between 0 and 100.`;
      }
    }

    return "";
  }, [formData, outcomeData, renovation?.has_plan, selectedPlan, t]);

  /* =======================================================
     SAVE INFORMATION
     ======================================================= */

  const saveInformation = React.useCallback(
    async (accessToken) => {
      const payload = {
        external_oss_name: formData.external_oss_name.trim(),

        external_oss_country_code: formData.external_oss_country_code
          .trim()
          .toUpperCase(),

        external_oss_contact_name: formData.external_oss_contact_name.trim(),

        external_oss_email: formData.external_oss_email.trim(),

        external_oss_phone: formData.external_oss_phone.trim(),

        external_oss_website: formData.external_oss_website.trim(),

        renovation_summary: formData.renovation_summary.trim(),

        planned_renovation_cost: formData.planned_renovation_cost,

        expected_start_date: formData.expected_start_date || null,

        expected_completion_date: formData.expected_completion_date || null,

        notes: formData.notes.trim(),
      };

      const existing = Boolean(renovation?.id);

      const url = existing
        ? `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovation.id}/`
        : `${API_BASE_URL}/api/homeowner/` + "external-renovations/";

      const response = await fetch(url, {
        method: existing ? "PATCH" : "POST",

        headers: {
          Authorization: `Bearer ${accessToken}`,

          "Content-Type": "application/json",

          Accept: "application/json",
        },

        body: JSON.stringify(payload),
      });

      const data = await parseApiResponse(response);

      return data?.external_renovation || null;
    },
    [formData, renovation?.id],
  );

  /* =======================================================
     LOAD ONE RENOVATION
     ======================================================= */

  const loadRenovation = React.useCallback(
    async (renovationId, accessToken) => {
      const response = await fetch(
        `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovationId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,

            Accept: "application/json",
          },
        },
      );

      const data = await parseApiResponse(response);

      return data?.external_renovation || null;
    },
    [],
  );

  /* =======================================================
     UPLOAD PLAN
     ======================================================= */

  const uploadPlan = React.useCallback(
    async (renovationId, accessToken, file) => {
      const body = new FormData();

      body.append("file", file);

      const response = await fetch(
        `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovationId}/plan/`,
        {
          method: "PUT",

          headers: {
            Authorization: `Bearer ${accessToken}`,
          },

          body,
        },
      );

      await parseApiResponse(response);
    },
    [],
  );

  /* =======================================================
     SAVE OUTCOME
     ======================================================= */

  const saveOutcome = React.useCallback(
    async (renovationId, accessToken) => {
      const payload = {
        actual_start_date: outcomeData.actual_start_date || null,
        actual_completion_date: outcomeData.actual_completion_date || null,

        actual_renovation_cost:
          outcomeData.actual_renovation_cost === ""
            ? null
            : outcomeData.actual_renovation_cost,

        completed_works_summary: outcomeData.completed_works_summary.trim(),
        completion_notes: outcomeData.completion_notes.trim(),

        final_epc_label: outcomeData.final_epc_label.trim(),

        final_epc_energy_score:
          outcomeData.final_epc_energy_score === ""
            ? null
            : outcomeData.final_epc_energy_score,

        final_epc_certificate_number:
          outcomeData.final_epc_certificate_number.trim(),

        final_epc_issue_date: outcomeData.final_epc_issue_date || null,

        estimated_energy_savings_percent:
          outcomeData.estimated_energy_savings_percent === ""
            ? null
            : outcomeData.estimated_energy_savings_percent,

        estimated_co2_reduction_percent:
          outcomeData.estimated_co2_reduction_percent === ""
            ? null
            : outcomeData.estimated_co2_reduction_percent,
      };

      const response = await fetch(
        `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovationId}/outcome/`,
        {
          method: "PATCH",

          headers: {
            Authorization: `Bearer ${accessToken}`,

            "Content-Type": "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify(payload),
        },
      );

      return parseApiResponse(response);
    },
    [outcomeData],
  );

  /* =======================================================
     UPLOAD FINAL EPC
     ======================================================= */

  const uploadFinalEpc = React.useCallback(
    async (renovationId, accessToken, file) => {
      const body = new FormData();

      body.append("file", file);

      const response = await fetch(
        `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovationId}/final-epc/`,
        {
          method: "PUT",

          headers: {
            Authorization: `Bearer ${accessToken}`,
          },

          body,
        },
      );

      await parseApiResponse(response);
    },
    [],
  );

  /* =======================================================
     SUBMIT
     ======================================================= */

  const handleSubmit = React.useCallback(
    async (event) => {
      event.preventDefault();

      if (isSaving) {
        return;
      }

      const validationError = validateForm();

      if (validationError) {
        showToast({
          type: "error",

          title: t(
            "externalRenovation.toast.validationTitle",
            "Please check the information",
          ),

          message: validationError,
        });

        return;
      }

      const wasExisting = Boolean(renovation?.id);

      try {
        setIsSaving(true);

        const accessToken = await getAccessTokenSilently();

        let savedRenovation = await saveInformation(accessToken);

        if (!savedRenovation?.id) {
          throw new Error(
            t(
              "externalRenovation.messages.saveError",
              "The renovation information could not be saved.",
            ),
          );
        }

        if (selectedPlan) {
          await uploadPlan(savedRenovation.id, accessToken, selectedPlan);
        }

        if (renovation?.has_outcome || hasOutcomeValues(outcomeData)) {
          await saveOutcome(savedRenovation.id, accessToken);
        }

        if (selectedFinalEpc) {
          await uploadFinalEpc(
            savedRenovation.id,
            accessToken,
            selectedFinalEpc,
          );
        }

        savedRenovation = await loadRenovation(savedRenovation.id, accessToken);

        setRenovation(savedRenovation);

        setFormData(normalizeRenovationToForm(savedRenovation));

        setOutcomeData(normalizeOutcomeToForm(savedRenovation));

        clearSelectedPlan();

        clearSelectedFinalEpc();

        showToast({
          type: "success",

          title: wasExisting
            ? t(
                "externalRenovation.toast.updatedTitle",
                "Renovation information updated",
              )
            : t(
                "externalRenovation.toast.createdTitle",
                "Renovation information saved",
              ),

          message: savedRenovation?.dossier_id
            ? t(
                "externalRenovation.toast.savedLinked",
                "Your external renovation information has been saved successfully.",
              )
            : t(
                "externalRenovation.toast.savedUnlinked",
                "Your renovation information has been saved. You can link it to a dossier when one is available.",
              ),
        });
      } catch (error) {
        console.error("Could not save external renovation:", error);

        showToast({
          type: "error",

          title: t(
            "externalRenovation.toast.saveErrorTitle",
            "Could not save renovation",
          ),

          message:
            error?.message ||
            t(
              "externalRenovation.messages.saveError",
              "Could not save the renovation information.",
            ),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      clearSelectedFinalEpc,
      clearSelectedPlan,
      getAccessTokenSilently,
      isSaving,
      loadRenovation,
      outcomeData,
      renovation?.has_outcome,
      renovation?.id,
      saveInformation,
      saveOutcome,
      selectedFinalEpc,
      selectedPlan,
      showToast,
      t,
      uploadFinalEpc,
      uploadPlan,
      validateForm,
    ],
  );

  /* =======================================================
     EXPLICIT DOSSIER LINK
     ======================================================= */

  const handleLinkDossier = React.useCallback(async () => {
    if (!renovation?.id || !hasAvailableContext || isLinking) {
      return;
    }

    try {
      setIsLinking(true);

      const accessToken = await getAccessTokenSilently();

      const payload = dossierId
        ? {
            dossier_id: Number(dossierId),
          }
        : {
            loan_case_id: Number(loanCaseId),
          };

      const response = await fetch(
        `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovation.id}/` +
          "link-dossier/",
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${accessToken}`,

            "Content-Type": "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify(payload),
        },
      );

      const data = await parseApiResponse(response);

      const linkedRenovation =
        data?.external_renovation ||
        (await loadRenovation(renovation.id, accessToken));

      setRenovation(linkedRenovation);

      showToast({
        type: "success",

        title: t("externalRenovation.toast.linkedTitle", "Dossier linked"),

        message: t(
          "externalRenovation.messages.linked",
          "The renovation information is now linked to your dossier.",
        ),
      });
    } catch (error) {
      console.error("Could not link dossier:", error);

      showToast({
        type: "error",

        title: t(
          "externalRenovation.toast.linkErrorTitle",
          "Could not link dossier",
        ),

        message:
          error?.message ||
          t(
            "externalRenovation.messages.linkError",
            "Could not link the renovation information to your dossier.",
          ),
      });
    } finally {
      setIsLinking(false);
    }
  }, [
    dossierId,
    getAccessTokenSilently,
    hasAvailableContext,
    isLinking,
    loadRenovation,
    loanCaseId,
    renovation?.id,
    showToast,
    t,
  ]);

  /* =======================================================
     VIEW PLAN
     ======================================================= */

  const handleViewPlan = React.useCallback(async () => {
    if (!renovation?.id || isOpeningPlan) {
      return;
    }

    const viewer = window.open("", "_blank");

    try {
      setIsOpeningPlan(true);

      const accessToken = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovation.id}/plan/file/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "externalRenovation.messages.planOpenError",
              "Could not open the renovation plan.",
            ),
        );
      }

      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);

      if (viewer) {
        viewer.location.href = objectUrl;
      } else {
        window.open(objectUrl, "_blank");
      }

      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (error) {
      if (viewer) {
        viewer.close();
      }

      showToast({
        type: "error",

        title: t(
          "externalRenovation.toast.planOpenErrorTitle",
          "Could not open renovation plan",
        ),

        message:
          error?.message ||
          t(
            "externalRenovation.messages.planOpenError",
            "Could not open the renovation plan.",
          ),
      });
    } finally {
      setIsOpeningPlan(false);
    }
  }, [getAccessTokenSilently, isOpeningPlan, renovation?.id, showToast, t]);

  /* =======================================================
     VIEW FINAL EPC
     ======================================================= */

  const handleViewFinalEpc = React.useCallback(async () => {
    if (!renovation?.id || isOpeningFinalEpc) {
      return;
    }

    const viewer = window.open("", "_blank");

    try {
      setIsOpeningFinalEpc(true);

      const accessToken = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE_URL}/api/homeowner/` +
          `external-renovations/${renovation.id}/final-epc/file/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "externalRenovation.messages.finalEpcOpenError",
              "Could not open the final EPC document.",
            ),
        );
      }

      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);

      if (viewer) {
        viewer.location.href = objectUrl;
      } else {
        window.open(objectUrl, "_blank");
      }

      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (error) {
      if (viewer) {
        viewer.close();
      }

      showToast({
        type: "error",

        title: t(
          "externalRenovation.toast.finalEpcOpenErrorTitle",
          "Could not open final EPC",
        ),

        message:
          error?.message ||
          t(
            "externalRenovation.messages.finalEpcOpenError",
            "Could not open the final EPC document.",
          ),
      });
    } finally {
      setIsOpeningFinalEpc(false);
    }
  }, [getAccessTokenSilently, isOpeningFinalEpc, renovation?.id, showToast, t]);

  const hasOutcomeDetails = React.useMemo(
    () => hasOutcomeValues(outcomeData),
    [outcomeData],
  );

  const hasFinalEpc = Boolean(
    renovation?.outcome?.has_final_epc ||
    renovation?.outcome?.final_epc ||
    selectedFinalEpc,
  );

  const epcCountryCode =
    dashboardSummary?.market_region?.country_code ||
    formData.external_oss_country_code ||
    "";

  const epcLabelOptions = React.useMemo(
    () => getEpcLabelOptions(epcCountryCode),
    [epcCountryCode],
  );

  /* =======================================================
     STATUS
     ======================================================= */

  const statusLabel = React.useMemo(() => {
    if (isLinked) {
      return t("externalRenovation.status.linked", "Linked to dossier");
    }

    if (hasAvailableContext) {
      return t("externalRenovation.status.readyToLink", "Ready to link");
    }

    return t("externalRenovation.status.notLinked", "No dossier linked");
  }, [hasAvailableContext, isLinked, t]);

  /* =======================================================
     LOADING
     ======================================================= */

  if (isAuthLoading || isLoading) {
    return (
      <div className="hb-ext-reno hb-ext-reno--loading">
        <motion.div
          className="hb-ext-reno__loader"
          initial={{
            opacity: 0,
            scale: 0.97,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
        >
          <div className="hb-ext-reno__loader-icon">
            <Loader2 size={27} />
          </div>

          <span>
            {t(
              "externalRenovation.loading",
              "Loading renovation information...",
            )}
          </span>
        </motion.div>
      </div>
    );
  }

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <motion.main
      className="hb-ext-reno"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="hb-ext-reno__shell">
        {/* =================================================
            BACK
            ================================================= */}

        <motion.div
          className="hb-ext-reno__navigation"
          variants={sectionVariants}
        >
          <button
            type="button"
            className="hb-ext-reno__back"
            onClick={() => navigate("/hb/dashboard")}
          >
            <ArrowLeft size={18} />

            <span>{t("externalRenovation.back", "Back to dashboard")}</span>
          </button>
        </motion.div>

        {/* =================================================
            HERO
            ================================================= */}

        <motion.section
          className="hb-ext-reno__hero"
          variants={sectionVariants}
        >
          <div className="hb-ext-reno__hero-glow" />

          <div className="hb-ext-reno__hero-icon">
            <Building2 size={30} />
          </div>

          <div className="hb-ext-reno__hero-content">
            <span className="hb-ext-reno__eyebrow">
              {t(
                "externalRenovation.hero.eyebrow",
                "External renovation support",
              )}
            </span>

            <h1>
              {t(
                "externalRenovation.hero.title",
                "Working with a One-Stop Shop outside LEG-UP",
              )}
            </h1>

            <p>
              {t(
                "externalRenovation.hero.description",
                "Provide information about your external One-Stop Shop and your planned renovation. Once linked to your dossier, this information can be made available to your lender.",
              )}
            </p>
          </div>

          <div className="hb-ext-reno__hero-badge">
            <ShieldCheck size={16} />

            <span>
              {t(
                "externalRenovation.hero.homebuyerProvided",
                "Homebuyer provided",
              )}
            </span>
          </div>
        </motion.section>

        {/* =================================================
            CONTENT
            ================================================= */}

        <div className="hb-ext-reno__layout">
          {/* ===============================================
              FORM
              =============================================== */}

          <motion.form
            className="hb-ext-reno__form"
            onSubmit={handleSubmit}
            variants={sectionVariants}
          >
            {/* =============================================
                OSS INFORMATION
                ============================================= */}

            <motion.section
              className="hb-ext-reno__card"
              variants={sectionVariants}
            >
              <SectionHeader
                number="01"
                icon={Building2}
                title={t(
                  "externalRenovation.oss.title",
                  "External One-Stop Shop information",
                )}
                description={t(
                  "externalRenovation.oss.description",
                  "Tell us which external One-Stop Shop or renovation advisor is supporting your renovation.",
                )}
              />

              <div className="hb-ext-reno__fields">
                <Field
                  label={t(
                    "externalRenovation.fields.ossName",
                    "External One-Stop Shop / renovation advisor",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.ossName",
                    "Enter the official name of the One-Stop Shop, renovation advisor or organisation supporting your renovation outside LEG-UP.",
                  )}
                  required
                  wide
                >
                  <div className="hb-ext-reno__input">
                    <Building2 size={18} />

                    <input
                      type="text"
                      name="external_oss_name"
                      value={formData.external_oss_name}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.ossName",
                        "Organisation name",
                      )}
                      required
                    />
                  </div>
                </Field>

                <Field
                  label={t("externalRenovation.fields.country", "Country")}
                  tooltip={t(
                    "externalRenovation.tooltips.country",
                    "Enter the two-letter country code of the country where the external One-Stop Shop operates, for example BE or ES.",
                  )}
                  hint={t("externalRenovation.hints.country", "2-letter code")}
                  required
                >
                  <div className="hb-ext-reno__input">
                    <MapPin size={18} />

                    <input
                      type="text"
                      name="external_oss_country_code"
                      value={formData.external_oss_country_code}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.country",
                        "BE",
                      )}
                      maxLength={2}
                      required
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.contactPerson",
                    "Contact person",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.contactPerson",
                    "Provide the name of your main contact person at the external One-Stop Shop, if available.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <UserRound size={18} />

                    <input
                      type="text"
                      name="external_oss_contact_name"
                      value={formData.external_oss_contact_name}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.contactPerson",
                        "Contact name",
                      )}
                    />
                  </div>
                </Field>

                <Field
                  label={t("externalRenovation.fields.email", "Email")}
                  tooltip={t(
                    "externalRenovation.tooltips.email",
                    "Provide the email address of the external One-Stop Shop or your contact person, if available.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <Mail size={18} />

                    <input
                      type="email"
                      name="external_oss_email"
                      value={formData.external_oss_email}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.email",
                        "contact@example.com",
                      )}
                    />
                  </div>
                </Field>

                <Field
                  label={t("externalRenovation.fields.phone", "Telephone")}
                  tooltip={t(
                    "externalRenovation.tooltips.phone",
                    "Provide a telephone number for the external One-Stop Shop or your contact person, if available.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <Phone size={18} />

                    <input
                      type="tel"
                      name="external_oss_phone"
                      value={formData.external_oss_phone}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.phone",
                        "+32 ...",
                      )}
                    />
                  </div>
                </Field>

                <Field
                  label={t("externalRenovation.fields.website", "Website")}
                  tooltip={t(
                    "externalRenovation.tooltips.website",
                    "Provide the external One-Stop Shop or renovation advisor website, if available.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <ExternalLink size={18} />

                    <input
                      type="url"
                      name="external_oss_website"
                      value={formData.external_oss_website}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.website",
                        "https://...",
                      )}
                    />
                  </div>
                </Field>
              </div>
            </motion.section>

            {/* =============================================
                RENOVATION INFORMATION
                ============================================= */}

            <motion.section
              className="hb-ext-reno__card"
              variants={sectionVariants}
            >
              <SectionHeader
                number="02"
                icon={FileText}
                title={t(
                  "externalRenovation.renovation.title",
                  "Renovation information",
                )}
                description={t(
                  "externalRenovation.renovation.description",
                  "Provide the main renovation information that may be needed for the financing assessment.",
                )}
              />

              <div className="hb-ext-reno__fields">
                <Field
                  label={t(
                    "externalRenovation.fields.summary",
                    "Renovation summary",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.summary",
                    "Summarise the planned renovation works, for example insulation, windows, heating systems, solar panels or other energy-efficiency measures.",
                  )}
                  required
                  wide
                >
                  <textarea
                    className="hb-ext-reno__textarea"
                    name="renovation_summary"
                    value={formData.renovation_summary}
                    onChange={handleChange}
                    rows={5}
                    placeholder={t(
                      "externalRenovation.placeholders.summary",
                      "Briefly describe the planned renovation works...",
                    )}
                    required
                  />
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.cost",
                    "Planned renovation cost",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.cost",
                    "Enter the total estimated cost of the planned renovation according to the current renovation plan.",
                  )}
                  required
                >
                  <div className="hb-ext-reno__input">
                    <Euro size={18} />

                    <input
                      type="text"
                      inputMode="decimal"
                      name="planned_renovation_cost"
                      value={formatDecimalInput(
                        formData.planned_renovation_cost,
                      )}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.cost",
                        "80,000",
                      )}
                      autoComplete="off"
                      required
                    />
                  </div>
                </Field>

                <div className="hb-ext-reno__desktop-spacer" />

                <Field
                  label={t(
                    "externalRenovation.fields.startDate",
                    "Expected start date",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.startDate",
                    "Enter the expected date on which renovation works are planned to begin, if known.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <CalendarDays size={18} />

                    <input
                      type="date"
                      name="expected_start_date"
                      value={formData.expected_start_date}
                      onChange={handleChange}
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.completionDate",
                    "Expected completion date",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.completionDate",
                    "Enter the expected date by which the renovation works should be completed, if known.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <CalendarDays size={18} />

                    <input
                      type="date"
                      name="expected_completion_date"
                      value={formData.expected_completion_date}
                      onChange={handleChange}
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.notes",
                    "Additional notes",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.notes",
                    "Add any other information concerning the renovation, the external One-Stop Shop or the planned works that may be useful for the lender.",
                  )}
                  wide
                >
                  <textarea
                    className="hb-ext-reno__textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    placeholder={t(
                      "externalRenovation.placeholders.notes",
                      "Add any additional information that may be useful...",
                    )}
                  />
                </Field>
              </div>
            </motion.section>

            {/* =============================================
                RENOVATION PLAN
                ============================================= */}

            <motion.section
              className="hb-ext-reno__card"
              variants={sectionVariants}
            >
              <SectionHeader
                number="03"
                icon={UploadCloud}
                title={t("externalRenovation.plan.title", "Renovation plan")}
                description={t(
                  "externalRenovation.plan.description",
                  "Upload the renovation plan prepared with your external renovation advisor. This document will support the lender's financing assessment.",
                )}
              />

              {renovation?.has_plan && renovation?.plan && !selectedPlan && (
                <motion.div
                  className="hb-ext-reno__existing-file"
                  initial={{
                    opacity: 0,
                    y: 8,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                >
                  <div className="hb-ext-reno__file-icon">
                    <FileText size={23} />
                  </div>

                  <div className="hb-ext-reno__file-copy">
                    <strong>{renovation.plan.original_filename}</strong>

                    <span>
                      {t(
                        "externalRenovation.plan.currentFile",
                        "Current renovation plan",
                      )}

                      {renovation.plan.size_bytes
                        ? ` · ${formatFileSize(renovation.plan.size_bytes)}`
                        : ""}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="hb-ext-reno__secondary-action"
                    onClick={handleViewPlan}
                    disabled={isOpeningPlan}
                  >
                    {isOpeningPlan ? (
                      <Loader2 size={16} className="hb-ext-reno__spinner" />
                    ) : (
                      <ExternalLink size={16} />
                    )}

                    <span>{t("externalRenovation.actions.view", "View")}</span>
                  </button>
                </motion.div>
              )}

              <motion.div
                className={[
                  "hb-ext-reno__upload",

                  selectedPlan ? "hb-ext-reno__upload--selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                layout
              >
                <input
                  ref={fileInputRef}
                  id="external-renovation-plan-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  onChange={handlePlanSelected}
                  hidden
                />

                <AnimatePresence mode="wait">
                  {selectedPlan ? (
                    <motion.div
                      key="selected"
                      className="hb-ext-reno__upload-inner"
                      initial={{
                        opacity: 0,
                        scale: 0.98,
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.98,
                      }}
                    >
                      <div className="hb-ext-reno__upload-icon hb-ext-reno__upload-icon--success">
                        <CircleCheck size={27} />
                      </div>

                      <div className="hb-ext-reno__upload-copy">
                        <strong>{selectedPlan.name}</strong>

                        <span>{formatFileSize(selectedPlan.size)}</span>
                      </div>

                      <button
                        type="button"
                        className="hb-ext-reno__remove-file"
                        onClick={clearSelectedPlan}
                        aria-label={t(
                          "externalRenovation.actions.removeFile",
                          "Remove file",
                        )}
                      >
                        <X size={18} />
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="empty"
                      className="hb-ext-reno__upload-inner"
                      initial={{
                        opacity: 0,
                      }}
                      animate={{
                        opacity: 1,
                      }}
                      exit={{
                        opacity: 0,
                      }}
                    >
                      <div className="hb-ext-reno__upload-icon">
                        <UploadCloud size={28} />
                      </div>

                      <div className="hb-ext-reno__upload-copy">
                        <strong>
                          {renovation?.has_plan
                            ? t(
                                "externalRenovation.plan.replace",
                                "Replace renovation plan",
                              )
                            : t(
                                "externalRenovation.plan.upload",
                                "Upload renovation plan",
                              )}
                        </strong>

                        <span>
                          {t(
                            "externalRenovation.plan.formats",
                            "PDF, PNG or JPG · Maximum 20 MB",
                          )}
                        </span>
                      </div>

                      <label
                        htmlFor="external-renovation-plan-file"
                        className="hb-ext-reno__choose-file"
                      >
                        {t(
                          "externalRenovation.actions.chooseFile",
                          "Choose file",
                        )}
                      </label>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <div className="hb-ext-reno__document-note">
                <Info size={16} />

                <span>
                  {t(
                    "externalRenovation.plan.notice",
                    "The renovation plan is required before the renovation information can be considered complete.",
                  )}
                </span>
              </div>
            </motion.section>

            {/* =============================================
                RENOVATION OUTCOME
                ============================================= */}

            <motion.section
              className="hb-ext-reno__card"
              variants={sectionVariants}
            >
              <SectionHeader
                number="04"
                icon={CircleCheck}
                title={t(
                  "externalRenovation.outcome.title",
                  "Renovation outcome",
                )}
                description={t(
                  "externalRenovation.outcome.description",
                  "Add the actual renovation results as the works progress or once they are completed. These details support lender follow-up and future impact reporting.",
                )}
              />

              <div className="hb-ext-reno__outcome-note">
                <Info size={17} />

                <div>
                  <strong>
                    {t(
                      "externalRenovation.outcome.noticeTitle",
                      "Complete this section when information becomes available",
                    )}
                  </strong>

                  <span>
                    {t(
                      "externalRenovation.outcome.notice",
                      "The planned renovation information above remains unchanged. This section records what was actually delivered, including the final EPC.",
                    )}
                  </span>
                </div>
              </div>

              <div className="hb-ext-reno__fields">
                <Field
                  label={t(
                    "externalRenovation.fields.actualStartDate",
                    "Actual start date",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.actualStartDate",
                    "Enter the date on which the renovation works actually started, if known.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <CalendarDays size={18} />

                    <input
                      type="date"
                      name="actual_start_date"
                      value={outcomeData.actual_start_date}
                      onChange={handleOutcomeChange}
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.actualCompletionDate",
                    "Actual completion date",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.actualCompletionDate",
                    "Enter the date on which the renovation works were actually completed.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <CalendarDays size={18} />

                    <input
                      type="date"
                      name="actual_completion_date"
                      value={outcomeData.actual_completion_date}
                      onChange={handleOutcomeChange}
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.actualCost",
                    "Actual renovation cost",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.actualCost",
                    "Enter the final known cost of the renovation works. This can differ from the planned renovation cost.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <Euro size={18} />

                    <input
                      type="text"
                      inputMode="decimal"
                      name="planned_renovation_cost"
                      value={formatDecimalInput(
                        formData.planned_renovation_cost,
                      )}
                      onChange={handleChange}
                      placeholder={t(
                        "externalRenovation.placeholders.cost",
                        "80,000",
                      )}
                      autoComplete="off"
                      required
                    />
                  </div>
                </Field>

                <div className="hb-ext-reno__desktop-spacer" />

                <Field
                  label={t(
                    "externalRenovation.fields.completedWorks",
                    "Completed works summary",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.completedWorks",
                    "Describe the renovation measures that were actually completed, especially where they differ from the original renovation plan.",
                  )}
                  wide
                >
                  <textarea
                    className="hb-ext-reno__textarea"
                    name="completed_works_summary"
                    value={outcomeData.completed_works_summary}
                    onChange={handleOutcomeChange}
                    rows={4}
                    placeholder={t(
                      "externalRenovation.placeholders.completedWorks",
                      "Summarise the works that were actually completed...",
                    )}
                  />
                </Field>
              </div>

              <div className="hb-ext-reno__subsection-title">
                <div>
                  <Gauge size={18} />
                </div>

                <div>
                  <strong>
                    {t(
                      "externalRenovation.outcome.epcTitle",
                      "Final energy performance",
                    )}
                  </strong>

                  <span>
                    {t(
                      "externalRenovation.outcome.epcDescription",
                      "Record the final EPC values and upload the certificate issued after the renovation.",
                    )}
                  </span>
                </div>
              </div>

              <div className="hb-ext-reno__fields">
                <Field
                  label={t(
                    "externalRenovation.fields.finalEpcLabel",
                    "Final EPC label",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.finalEpcLabel",
                    "Enter the energy performance label shown on the final EPC certificate.",
                  )}
                  hint={t(
                    "externalRenovation.hints.finalEpcLabel",
                    "e.g. A, B, C",
                  )}
                >
                  <div className="hb-ext-reno__input hb-ext-reno__input--select">
                    <FileText size={18} />

                    <select
                      name="final_epc_label"
                      value={outcomeData.final_epc_label}
                      onChange={handleOutcomeChange}
                    >
                      <option value="">
                        {t(
                          "externalRenovation.placeholders.selectEpcLabel",
                          "Select EPC label",
                        )}
                      </option>

                      {epcLabelOptions.map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.finalEpcScore",
                    "Final EPC energy score",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.finalEpcScore",
                    "Enter the numerical energy score from the final EPC, where available.",
                  )}
                  hint={t(
                    "externalRenovation.hints.finalEpcScore",
                    "kWh/m²/year",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <Gauge size={18} />

                    <input
                      type="text"
                      inputMode="decimal"
                      name="final_epc_energy_score"
                      value={formatDecimalInput(
                        outcomeData.final_epc_energy_score,
                      )}
                      onChange={handleOutcomeChange}
                      placeholder={t(
                        "externalRenovation.placeholders.finalEpcScore",
                        "145",
                      )}
                      autoComplete="off"
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.finalEpcCertificateNumber",
                    "EPC certificate number",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.finalEpcCertificateNumber",
                    "Enter the certificate or reference number shown on the final EPC, if available.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <FileText size={18} />

                    <input
                      type="text"
                      name="final_epc_certificate_number"
                      value={outcomeData.final_epc_certificate_number}
                      onChange={handleOutcomeChange}
                      placeholder={t(
                        "externalRenovation.placeholders.finalEpcCertificateNumber",
                        "Certificate reference",
                      )}
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.finalEpcIssueDate",
                    "Final EPC issue date",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.finalEpcIssueDate",
                    "Enter the issue date shown on the final EPC certificate.",
                  )}
                >
                  <div className="hb-ext-reno__input">
                    <CalendarDays size={18} />

                    <input
                      type="date"
                      name="final_epc_issue_date"
                      value={outcomeData.final_epc_issue_date}
                      onChange={handleOutcomeChange}
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.energySavings",
                    "Estimated energy savings",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.energySavings",
                    "Enter the estimated percentage reduction in energy use after the renovation, if this has been calculated.",
                  )}
                  hint="%"
                >
                  <div className="hb-ext-reno__input">
                    <Percent size={18} />

                    <input
                      type="text"
                      inputMode="decimal"
                      name="estimated_energy_savings_percent"
                      value={formatDecimalInput(
                        outcomeData.estimated_energy_savings_percent,
                      )}
                      onChange={handleOutcomeChange}
                      placeholder="0"
                      autoComplete="off"
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.co2Reduction",
                    "Estimated CO₂ reduction",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.co2Reduction",
                    "Enter the estimated percentage reduction in CO₂ emissions after the renovation, if this has been calculated.",
                  )}
                  hint="%"
                >
                  <div className="hb-ext-reno__input">
                    <Percent size={18} />

                    <input
                      type="text"
                      inputMode="decimal"
                      name="estimated_co2_reduction_percent"
                      value={formatDecimalInput(
                        outcomeData.estimated_co2_reduction_percent,
                      )}
                      onChange={handleOutcomeChange}
                      placeholder="0"
                      autoComplete="off"
                    />
                  </div>
                </Field>

                <Field
                  label={t(
                    "externalRenovation.fields.completionNotes",
                    "Completion notes",
                  )}
                  tooltip={t(
                    "externalRenovation.tooltips.completionNotes",
                    "Add any final information that may be useful for the lender or for renovation impact reporting.",
                  )}
                  wide
                >
                  <textarea
                    className="hb-ext-reno__textarea"
                    name="completion_notes"
                    value={outcomeData.completion_notes}
                    onChange={handleOutcomeChange}
                    rows={4}
                    placeholder={t(
                      "externalRenovation.placeholders.completionNotes",
                      "Add any final project notes...",
                    )}
                  />
                </Field>
              </div>

              <div className="hb-ext-reno__subsection-title hb-ext-reno__subsection-title--document">
                <div>
                  <UploadCloud size={18} />
                </div>

                <div>
                  <strong>
                    {t(
                      "externalRenovation.outcome.finalEpcDocumentTitle",
                      "Final EPC certificate",
                    )}
                  </strong>

                  <span>
                    {t(
                      "externalRenovation.outcome.finalEpcDocumentDescription",
                      "Upload the EPC certificate issued after completion of the renovation.",
                    )}
                  </span>
                </div>
              </div>

              {renovation?.outcome?.has_final_epc &&
                renovation?.outcome?.final_epc &&
                !selectedFinalEpc && (
                  <motion.div
                    className="hb-ext-reno__existing-file"
                    initial={{
                      opacity: 0,
                      y: 8,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                  >
                    <div className="hb-ext-reno__file-icon">
                      <FileText size={23} />
                    </div>

                    <div className="hb-ext-reno__file-copy">
                      <strong>
                        {renovation.outcome.final_epc.original_filename}
                      </strong>

                      <span>
                        {t(
                          "externalRenovation.outcome.currentFinalEpc",
                          "Current final EPC certificate",
                        )}

                        {renovation.outcome.final_epc.size_bytes
                          ? ` · ${formatFileSize(
                              renovation.outcome.final_epc.size_bytes,
                            )}`
                          : ""}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="hb-ext-reno__secondary-action"
                      onClick={handleViewFinalEpc}
                      disabled={isOpeningFinalEpc}
                    >
                      {isOpeningFinalEpc ? (
                        <Loader2 size={16} className="hb-ext-reno__spinner" />
                      ) : (
                        <ExternalLink size={16} />
                      )}

                      <span>
                        {t("externalRenovation.actions.view", "View")}
                      </span>
                    </button>
                  </motion.div>
                )}

              <motion.div
                className={[
                  "hb-ext-reno__upload",

                  selectedFinalEpc ? "hb-ext-reno__upload--selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                layout
              >
                <input
                  ref={finalEpcInputRef}
                  id="external-renovation-final-epc-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  onChange={handleFinalEpcSelected}
                  hidden
                />

                <AnimatePresence mode="wait">
                  {selectedFinalEpc ? (
                    <motion.div
                      key="selected-final-epc"
                      className="hb-ext-reno__upload-inner"
                      initial={{
                        opacity: 0,
                        scale: 0.98,
                      }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.98,
                      }}
                    >
                      <div className="hb-ext-reno__upload-icon hb-ext-reno__upload-icon--success">
                        <CircleCheck size={27} />
                      </div>

                      <div className="hb-ext-reno__upload-copy">
                        <strong>{selectedFinalEpc.name}</strong>

                        <span>{formatFileSize(selectedFinalEpc.size)}</span>
                      </div>

                      <button
                        type="button"
                        className="hb-ext-reno__remove-file"
                        onClick={clearSelectedFinalEpc}
                        aria-label={t(
                          "externalRenovation.actions.removeFinalEpc",
                          "Remove final EPC file",
                        )}
                      >
                        <X size={18} />
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="empty-final-epc"
                      className="hb-ext-reno__upload-inner"
                      initial={{
                        opacity: 0,
                      }}
                      animate={{
                        opacity: 1,
                      }}
                      exit={{
                        opacity: 0,
                      }}
                    >
                      <div className="hb-ext-reno__upload-icon">
                        <UploadCloud size={28} />
                      </div>

                      <div className="hb-ext-reno__upload-copy">
                        <strong>
                          {renovation?.outcome?.has_final_epc
                            ? t(
                                "externalRenovation.outcome.replaceFinalEpc",
                                "Replace final EPC certificate",
                              )
                            : t(
                                "externalRenovation.outcome.uploadFinalEpc",
                                "Upload final EPC certificate",
                              )}
                        </strong>

                        <span>
                          {t(
                            "externalRenovation.outcome.finalEpcFormats",
                            "PDF, PNG or JPG · Maximum 20 MB",
                          )}
                        </span>
                      </div>

                      <label
                        htmlFor="external-renovation-final-epc-file"
                        className="hb-ext-reno__choose-file"
                      >
                        {t(
                          "externalRenovation.actions.chooseFile",
                          "Choose file",
                        )}
                      </label>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <div className="hb-ext-reno__document-note">
                <Info size={16} />

                <span>
                  {t(
                    "externalRenovation.outcome.finalEpcNotice",
                    "The final EPC and outcome values are kept separately from the original renovation plan so the lender can compare planned and delivered results.",
                  )}
                </span>
              </div>
            </motion.section>

            {/* =============================================
                SAVE
                ============================================= */}

            <motion.div
              className="hb-ext-reno__save-bar"
              variants={sectionVariants}
            >
              <div className="hb-ext-reno__save-note">
                <ShieldCheck size={18} />

                <span>
                  {t(
                    "externalRenovation.disclaimer",
                    "This information is provided by you for an external One-Stop Shop that is not registered in LEG-UP.",
                  )}
                </span>
              </div>

              <button
                type="submit"
                className="hb-ext-reno__save-button"
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 size={19} className="hb-ext-reno__spinner" />
                ) : (
                  <Save size={19} />
                )}

                <span>
                  {isSaving
                    ? t("externalRenovation.actions.saving", "Saving...")
                    : renovation
                      ? t(
                          "externalRenovation.actions.saveChanges",
                          "Save changes",
                        )
                      : t(
                          "externalRenovation.actions.save",
                          "Save renovation information",
                        )}
                </span>
              </button>
            </motion.div>
          </motion.form>

          {/* ===============================================
              SIDEBAR
              =============================================== */}

          <motion.aside
            className="hb-ext-reno__sidebar"
            variants={sectionVariants}
          >
            {/* =============================================
                CONNECTION
                ============================================= */}

            <div className="hb-ext-reno__status-card">
              <div className="hb-ext-reno__status-heading">
                <div>
                  <span>
                    {t(
                      "externalRenovation.sidebar.statusEyebrow",
                      "Financing connection",
                    )}
                  </span>

                  <h3>{statusLabel}</h3>
                </div>

                <div
                  className={[
                    "hb-ext-reno__status-dot",

                    isLinked ? "hb-ext-reno__status-dot--linked" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {isLinked ? <Check size={16} /> : <Link2 size={16} />}
                </div>
              </div>

              {isLinked ? (
                <div className="hb-ext-reno__status-body">
                  <p>
                    {t(
                      "externalRenovation.sidebar.linkedDescription",
                      "This renovation is linked to your financing dossier. Your lender can access the renovation information through the corresponding loan case.",
                    )}
                  </p>

                  {renovation?.dossier_code && (
                    <div className="hb-ext-reno__reference">
                      <span>
                        {t("externalRenovation.sidebar.dossier", "Dossier")}
                      </span>

                      <strong>{renovation.dossier_code}</strong>
                    </div>
                  )}
                </div>
              ) : hasAvailableContext ? (
                <div className="hb-ext-reno__status-body">
                  <p>
                    {t(
                      "externalRenovation.sidebar.availableDescription",
                      "A financing dossier is available. Link this renovation when you want the lender to access the information and renovation plan.",
                    )}
                  </p>

                  {dossierCode && (
                    <div className="hb-ext-reno__reference">
                      <span>
                        {t("externalRenovation.sidebar.dossier", "Dossier")}
                      </span>

                      <strong>{dossierCode}</strong>
                    </div>
                  )}

                  {!dossierCode && loanCaseReference && (
                    <div className="hb-ext-reno__reference">
                      <span>
                        {t("externalRenovation.sidebar.loanCase", "Loan case")}
                      </span>

                      <strong>{loanCaseReference}</strong>
                    </div>
                  )}

                  {renovation?.id ? (
                    <button
                      type="button"
                      className="hb-ext-reno__link-button"
                      onClick={handleLinkDossier}
                      disabled={isLinking}
                    >
                      {isLinking ? (
                        <Loader2 size={17} className="hb-ext-reno__spinner" />
                      ) : (
                        <Link2 size={17} />
                      )}

                      <span>
                        {isLinking
                          ? t(
                              "externalRenovation.actions.linking",
                              "Linking...",
                            )
                          : t(
                              "externalRenovation.actions.linkDossier",
                              "Link to dossier",
                            )}
                      </span>
                    </button>
                  ) : (
                    <div className="hb-ext-reno__save-first">
                      <Info size={16} />

                      <span>
                        {t(
                          "externalRenovation.sidebar.saveFirst",
                          "Save the renovation information first. You can then link it to the available dossier.",
                        )}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="hb-ext-reno__status-body">
                  <p>
                    {t(
                      "externalRenovation.sidebar.noDossierDescription",
                      "You can complete the renovation information now. When a dossier becomes available, you will be able to link it from this page.",
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* =============================================
                INFORMATION STATUS
                ============================================= */}

            <div className="hb-ext-reno__progress-card">
              <span className="hb-ext-reno__sidebar-eyebrow">
                {t("externalRenovation.sidebar.progress", "Information status")}
              </span>

              <div className="hb-ext-reno__progress-item">
                <div
                  className={
                    formData.external_oss_name.trim() ? "is-complete" : ""
                  }
                >
                  <Check size={14} />
                </div>

                <span>
                  {t(
                    "externalRenovation.sidebar.ossDetails",
                    "External One-Stop Shop details",
                  )}
                </span>
              </div>

              <div className="hb-ext-reno__progress-item">
                <div
                  className={
                    formData.renovation_summary.trim() &&
                    Number(formData.planned_renovation_cost) > 0
                      ? "is-complete"
                      : ""
                  }
                >
                  <Check size={14} />
                </div>

                <span>
                  {t(
                    "externalRenovation.sidebar.renovationDetails",
                    "Renovation details",
                  )}
                </span>
              </div>

              <div className="hb-ext-reno__progress-item">
                <div
                  className={
                    renovation?.has_plan || selectedPlan ? "is-complete" : ""
                  }
                >
                  <Check size={14} />
                </div>

                <span>
                  {t("externalRenovation.sidebar.plan", "Renovation plan")}
                </span>
              </div>

              <div className="hb-ext-reno__progress-item">
                <div className={isLinked ? "is-complete" : ""}>
                  <Check size={14} />
                </div>

                <span>
                  {t(
                    "externalRenovation.sidebar.financingLink",
                    "Financing link",
                  )}
                </span>
              </div>

              <div className="hb-ext-reno__progress-item">
                <div className={hasOutcomeDetails ? "is-complete" : ""}>
                  <Check size={14} />
                </div>

                <span>
                  {t(
                    "externalRenovation.sidebar.outcome",
                    "Renovation outcome",
                  )}
                </span>
              </div>

              <div className="hb-ext-reno__progress-item">
                <div className={hasFinalEpc ? "is-complete" : ""}>
                  <Check size={14} />
                </div>

                <span>
                  {t("externalRenovation.sidebar.finalEpc", "Final EPC")}
                </span>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </motion.main>
  );
}
